import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CoachCodeCard from '../components/CoachCodeCard';
import './DashboardPage.css';

function formatDate(dateValue) {
  if (!dateValue) return 'Unknown';
  const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function DashboardPage({ user }) {
  const [coachData, setCoachData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBrand, setEditBrand] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleSaveProfile = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'coaches', user.uid), {
        name: editName.trim(),
        brandName: editBrand.trim(),
      });
      setCoachData(prev => ({
        ...prev,
        name: editName.trim(),
        brandName: editBrand.trim(),
      }));
      setEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, 'coaches', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCoachData(data);

          if (data.coachCode) {
            const q = query(
              collection(db, 'students'),
              where('coachCode', '==', data.coachCode)
            );
            const snapshot = await getDocs(q);
            const studentList = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            studentList.sort((a, b) => {
              const nameA = (a.name || '').toLowerCase();
              const nameB = (b.name || '').toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setStudents(studentList);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.uid]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Navbar coachName={coachData?.brandName || coachData?.name || 'Coach'} />

      <div className="dashboard-content">
        <div className="welcome-section">
          {editing ? (
            <div className="profile-edit">
              <div className="profile-edit-group">
                <label>Your Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Your full name"
                  className="profile-edit-input"
                />
              </div>
              <div className="profile-edit-group">
                <label>Academy / Brand Name</label>
                <input
                  type="text"
                  value={editBrand}
                  onChange={(e) => setEditBrand(e.target.value)}
                  placeholder="Optional"
                  className="profile-edit-input"
                />
              </div>
              <div className="profile-edit-actions">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving || !editName.trim()}
                  className="profile-save-btn"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="profile-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="welcome-header">
                <h1>Welcome, {coachData?.name || 'Coach'}</h1>
                <button
                  className="profile-edit-btn"
                  onClick={() => {
                    setEditName(coachData?.name || '');
                    setEditBrand(coachData?.brandName || '');
                    setEditing(true);
                  }}
                  title="Edit profile"
                >
                  ✏️
                </button>
              </div>
              {coachData?.brandName && (
                <p className="brand-name">{coachData.brandName}</p>
              )}
            </>
          )}
        </div>

        <CoachCodeCard code={coachData?.coachCode || '------'} />

        <div className="roster-section">
          <h2>Your Students ({students.length})</h2>
          {students.length === 0 ? (
            <div className="empty-roster">
              <div className="empty-roster-icon">👥</div>
              <h3>No students connected yet</h3>
              <p>Share your coach code with your students. They'll enter it in the 3 Pillars Putting app to connect to you.</p>
            </div>
          ) : (
            <div className="student-list">
              {students.map((student) => (
                <div
                  key={student.id}
                  className="student-row clickable"
                  onClick={() => navigate(`/student/${student.id}`)}
                >
                  <div className="student-status">
                    <span className="status-dot active" title="Connected"></span>
                  </div>
                  <div className="student-info">
                    <span className="student-name">{student.name || 'Unnamed Student'}</span>
                    <span className="student-connected">
                      Connected {formatDate(student.connectedAt || student.createdAt)}
                    </span>
                  </div>
                  <div className="student-arrow">›</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
